import React, { useContext, useEffect, useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  IonAvatar,
  IonButton,
  IonContent,
  IonFooter,
  IonIcon,
  IonPage,
  IonText,
  IonTextarea,
  IonToolbar,
  IonInput,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import {
  camera,
  chevronForwardOutline,
  closeOutline,
  peopleOutline,
} from "ionicons/icons";
import { WerkerSchedulerHeader } from "../components/WerkHeader";
import { ProfileContext } from "../context/ProfileContext";
import "../theme/AppStyles.css";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { saveAnnouncement } from "../domain/announcements/announcementActions";
import { consumePendingAudienceSelection } from "../domain/announcements/announcementAudienceHandoff";

type AudienceAssociate = {
  WerkerUid: string;
  WerkerFirstName: string;
  WerkerLastName: string;
  WerkerProfilePicURL: string;
  WerkerCompany: string;
  WerkerBio: string;
  isSelected?: boolean;
};

type AnnouncementLocationState = {
  cameFrom?: string;
  mode?: "create" | "edit";
  announcementId?: string;
  selectedAssociates?: AudienceAssociate[];
  selectedCrewIds?: string[];
  recipientUids?: string[];
  draftTitle?: string;
  draftBody?: string;
  draftImageUrl?: string | null;
  draftIsPinned?: boolean;
};

const Announcement: React.FC<RouteComponentProps> = () => {
  const history = useHistory<AnnouncementLocationState>();
  const { profile, myAnnouncementPosts, myAssociates } = useContext(ProfileContext);
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();

  const locationState = (history.location.state || {}) as AnnouncementLocationState;
  const mode: "create" | "edit" = locationState.mode === "edit" ? "edit" : "create";
  const editingAnnouncementId = locationState.announcementId || "";
  const isEditMode = mode === "edit" && editingAnnouncementId.length > 0;

  const existingRecipientUids = Array.isArray(locationState.recipientUids) ? locationState.recipientUids : [];

  const [title, setTitle] = useState<string>(locationState.draftTitle || "");
  const [body, setBody] = useState<string>(locationState.draftBody || "");
  const [imageUrl, setImageUrl] = useState<string>(locationState.draftImageUrl || "");
  const [isPinned, setIsPinned] = useState<boolean>(Boolean(locationState.draftIsPinned));
  const [selectedAssociates, setSelectedAssociates] = useState<AudienceAssociate[]>(
    Array.isArray(locationState.selectedAssociates) ? locationState.selectedAssociates : []
  );
  const [selectedCrewIds, setSelectedCrewIds] = useState<string[]>(
    Array.isArray(locationState.selectedCrewIds) ? locationState.selectedCrewIds : []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const storage = getStorage();
  const isSchedulerMode = profile?.UserLastRoleScheduler === true;

  // Hydrate existing recipients into AudienceAssociate objects in edit mode
  useEffect(() => {
    if (!isEditMode) return;
    if (selectedAssociates.length > 0) return; // already hydrated (e.g. returning from picker)
    if (existingRecipientUids.length === 0) return;

    const associates = myAssociates || [];
    const hydrated: AudienceAssociate[] = existingRecipientUids
      .map((uid: string) => {
        const match = associates.find((a: any) => a.WerkerUid === uid);
        if (!match) {
          // Recipient may no longer be an associate — keep them with minimal info
          return {
            WerkerUid: uid,
            WerkerFirstName: "Unknown",
            WerkerLastName: "User",
            WerkerProfilePicURL: "",
            WerkerCompany: "",
            WerkerBio: "",
            isSelected: true,
          };
        }
        return {
          WerkerUid: match.WerkerUid,
          WerkerFirstName: match.WerkerFirstName || "",
          WerkerLastName: match.WerkerLastName || "",
          WerkerProfilePicURL: match.WerkerProfilePicURL || "",
          WerkerCompany: match.WerkerCompany || "",
          WerkerBio: match.WerkerBio || "",
          isSelected: true,
        };
      });

    setSelectedAssociates(hydrated);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, existingRecipientUids, myAssociates]);

  useEffect(() => {
    if (Array.isArray(locationState.selectedAssociates)) {
      setSelectedAssociates(locationState.selectedAssociates);
    }
    if (Array.isArray(locationState.selectedCrewIds)) {
      setSelectedCrewIds(locationState.selectedCrewIds);
    }
    if (typeof locationState.draftTitle === "string") {
      setTitle(locationState.draftTitle);
    }
    if (typeof locationState.draftBody === "string") {
      setBody(locationState.draftBody);
    }
    if (typeof locationState.draftImageUrl === "string") {
      setImageUrl(locationState.draftImageUrl);
    }
    if (typeof locationState.draftIsPinned === "boolean") {
      setIsPinned(locationState.draftIsPinned);
    }
  }, [locationState.selectedAssociates, locationState.selectedCrewIds, locationState.draftTitle, locationState.draftBody, locationState.draftImageUrl, locationState.draftIsPinned, selectedAssociates.length]);

  // Consume any pending audience selection handed back by the picker via
  // history.goBack(). Runs on every render but consume() only returns a payload
  // once, so this is a no-op except immediately after returning from the picker.
  useEffect(() => {
    const pending = consumePendingAudienceSelection();
    if (!pending) return;
    setSelectedAssociates(pending.selectedAssociates);
    setSelectedCrewIds(pending.selectedCrewIds);
  });

  // useMemo for selectedCount and selectedNamesPreview
  // These are kept for potential future use in the UI

  const bodyPreviewCount = useMemo(() => body.trim().length, [body]);

  const audienceCount = useMemo(() => {
    return selectedAssociates.length;
  }, [selectedAssociates.length]);

  const previewAuthorName = useMemo(() => {
    return `${profile?.FirstName || ""} ${profile?.LastName || ""}`.trim() || "You";
  }, [profile?.FirstName, profile?.LastName]);

  const previewDate = "Now";

  const openAudiencePicker = () => {
    // Push the picker. The picker will hand its result back via the
    // announcementAudienceHandoff store and call history.goBack(), so we
    // don't need to round-trip selections through location.state here.
    history.push({
      pathname: "/SelectAnnouncementAudience",
      state: {
        cameFrom: "Announcement",
        selectedAssociates,
        selectedCrewIds,
        // Preserve edit-mode context (read by the picker for any edit-aware UI)
        mode: isEditMode ? "edit" : "create",
        announcementId: editingAnnouncementId || undefined,
        recipientUids: existingRecipientUids.length > 0 ? existingRecipientUids : undefined,
      },
    });
  };

  const validateBeforeSend = async () => {
    if (!profile?.UserId) {
      await presentAlert({
        header: "Unable to Continue",
        message: "Your profile is not ready yet.",
        buttons: ["OK"],
      });
      return false;
    }

    if (!title.trim() && !body.trim()) {
      await presentAlert({
        header: "Missing Content",
        message: "Please enter a title or a message for this announcement.",
        buttons: ["OK"],
      });
      return false;
    }

    if (selectedAssociates.length === 0) {
      await presentAlert({
        header: "No Audience Selected",
        message: "Please choose at least one associate for this announcement.",
        buttons: ["OK"],
      });
      return false;
    }

    return true;
  };

  const handleSend = async () => {
    const isValid = await validateBeforeSend();
    if (!isValid) return;

    setIsSubmitting(true);

    try {
      const savedAnnouncementId = await saveAnnouncement({
        mode: isEditMode ? "edit" : "create",
        announcementId: isEditMode ? editingAnnouncementId : undefined,
        profile,
        title,
        body,
        imageUrl,
        isPinned,
        recipientUids: selectedAssociates.map((a) => a.WerkerUid).filter(Boolean),
        selectedCrewIds: selectedCrewIds || [],
        myAnnouncementPosts,
      });

      // The picker hands selections back via history.goBack() (no extra
      // /Announcement entry), so the stack on save is simply:
      //   Edit:   [..., AnnouncementDetail, Announcement(edit)]   -> goBack -> AnnouncementDetail (refreshes via onSnapshot)
      //   Create: [..., Chats, Announcement(create)]              -> goBack -> Chats
      if (isEditMode) {
        await presentToast({
          message: "Announcement updated",
          duration: 1500,
          position: "top",
          color: "medium",
        });

        history.goBack();
      } else {
        await presentToast({
          message: "Announcement sent",
          duration: 1500,
          position: "top",
          color: "medium",
        });

        history.goBack();
      }
    } catch (error) {
      console.error(isEditMode ? "Error updating announcement:" : "Error creating announcement:", error);
      await presentAlert({
        header: "Error",
        message: isEditMode
          ? "Something went wrong while updating the announcement."
          : "Something went wrong while sending the announcement.",
        buttons: ["OK"],
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePickAnnouncementPhoto = async () => {
    try {
      const photo = await Camera.getPhoto({
        quality: 75,
        width: 1200,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
      });

      if (!photo.dataUrl) return;

      const response = await fetch(photo.dataUrl);
      const blob = await response.blob();

      if (blob.size > 5 * 1024 * 1024) {
        await presentAlert({
          header: "Image Too Large",
          message: "Please choose an image under 5MB.",
          buttons: ["OK"],
        });
        return;
      }

      const filePath = `announcementImages/${Date.now()}.jpg`;
      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(storageRef);

      setImageUrl(downloadURL);

      await presentToast({
        message: "Photo added",
        duration: 1400,
        position: "top",
        color: "medium",
      });
    } catch (error) {
      // console.log("handlePickAnnouncementPhoto error:", error);
      await presentAlert({
        header: "Photo Error",
        message: "Unable to add photo right now.",
        buttons: ["OK"],
      });
    }
  };

  const handleRemoveAnnouncementPhoto = async () => {
    setImageUrl("");

    await presentToast({
      message: "Photo removed",
      duration: 1200,
      position: "top",
      color: "medium",
    });
  };

  return (
    <IonPage>
      <WerkerSchedulerHeader title={isEditMode ? "Edit Announcement" : "Create Announcement"} />

      <IonContent>
        <div
          style={{
            maxWidth: "840px",
            margin: "0 auto",
            padding: "12px 12px 110px",
          }}
        >
          <div
            style={{
              background: "var(--ion-item-background, var(--ion-background-color))",
              borderRadius: "14px",
              padding: "16px",
              border: "1px solid rgba(0,0,0,0.06)",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <IonText>
                <div style={{ fontWeight: 700, fontSize: "1rem" }}>Compose</div>
              </IonText>
            </div>

            <div style={{ marginBottom: 14 }}>
              <IonText
                color="medium"
              >
                <div style={{ fontSize: "0.86rem", fontWeight: 600, marginBottom: 6 }}>Title</div>
              </IonText>

              <IonInput
                value={title}
                spellCheck={true}
                autoCapitalize="words"
                onIonChange={(e: any) => setTitle(e.detail.value || "")}
                placeholder=" Enter title (optional)"
                maxlength={120}
                clearInput={true}
                style={{
                  // borderBottom: "1px solid",
                  "--padding-start": "0px",
                  "--padding-end": "0px",
                  // fontSize: "1.02rem",
                  fontWeight: 500,
                }}
              />
            </div>

            <div
              style={{
                borderTop: "1px solid rgba(0,0,0,0.06)",
                paddingTop: 14,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 6,
                }}
              >
                <IonText
                  color="medium"
                >
                  <div style={{ fontSize: "0.86rem", fontWeight: 600 }}>Message</div>
                </IonText>

                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {!imageUrl && (
                    <IonButton
                      // size="small"
                      fill="clear"
                      color={isSchedulerMode ? "secondarywarning" : "warning"}
                      onClick={handlePickAnnouncementPhoto}
                    >
                      {/* <IonLabel>
                        Add 
                      </IonLabel> */}
                      <IonIcon
                        icon={camera}
                        // slot="icon-only"
                      />
                    </IonButton>
                  )}

                  {!!imageUrl && (
                    <IonButton
                      size="small"
                      fill="clear"
                      color="medium"
                      onClick={handleRemoveAnnouncementPhoto}
                    >
                      <IonIcon
                        icon={closeOutline}
                        slot="start"
                      />
                      Remove Photo
                    </IonButton>
                  )}
                </div>
              </div>

              <IonTextarea
                value={body}
                onIonChange={(e: any) => setBody(e.detail.value || "")}
                placeholder=" Write your announcement..."
                autoGrow={true}
                autoCorrect="true"
                autoCapitalize="sentences"
                rows={3}
                maxlength={2000}
                style={{
                  // borderBottom: "1px solid",
                  "--padding-start": "0px",
                  "--padding-end": "0px",
                  fontSize: "0.98rem",
                  lineHeight: 1.5,
                }}
              />
              <IonText
                color="medium"
              >
                <div style={{ marginTop: 5, fontSize: "0.84rem" }}>{bodyPreviewCount}/2000 characters</div>
              </IonText>
              {!!imageUrl && (
                <div style={{ marginTop: 14 }}>
                  <img
                    src={imageUrl}
                    alt="Announcement preview"
                    style={{
                      width: "100%",
                      maxHeight: "300px",
                      objectFit: "cover",
                      borderRadius: "12px",
                      display: "block",
                    }}
                    onError={(e) => {
                      const el = e.currentTarget as HTMLImageElement;
                      el.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div
            onClick={() => openAudiencePicker()}
            style={{
              background: "var(--ion-item-background, var(--ion-background-color))",
              borderRadius: "14px",
              padding: "16px",
              border: "1px solid rgba(0,0,0,0.06)",
              marginBottom: "12px",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: "999px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(0,0,0,0.06)",
                    flexShrink: 0,
                  }}
                >
                  <IonIcon
                    icon={peopleOutline}
                    style={{ fontSize: "2rem" }}
                  />
                </div>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <IonText>
                    <div style={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1.2 }}>Audience</div>
                  </IonText>

                  <IonText
                    color="medium"
                  >
                    <div style={{ marginTop: 4, fontSize: "0.92rem", lineHeight: 1.35 }}>
                      {audienceCount > 0
                        ? `${audienceCount} recipient${audienceCount === 1 ? "" : "s"} selected`
                        : "No audience selected"}
                    </div>
                  </IonText>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  color: audienceCount === 0
                    ? "var(--ion-color-secondwarning)"
                    : "var(--ion-color-medium)",
                  flexShrink: 0,
                }}
              >
                <IonText
                  color={audienceCount === 0 ? "secondwarning" : "medium"}
                >
                  <div style={{ fontSize: "0.9rem", fontWeight: 600 }}>{isEditMode ? "Edit" : "Select"}</div>
                </IonText>
                <IonIcon
                  icon={chevronForwardOutline}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              background: "var(--ion-item-background, var(--ion-background-color))",
              borderRadius: "14px",
              padding: "16px",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <IonText>
              <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 14 }}>Preview</div>
            </IonText>

            <div
              style={{
                background: "var(--ion-background-color)",
                borderRadius: "14px",
                padding: "16px",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <IonAvatar
                  style={{ width: 42, height: 42, flexShrink: 0 }}
                >
                  <img
                    src={profile?.ProfilePicURL || "../assets/profilePic.png"}
                    alt={previewAuthorName}
                    style={{ objectFit: "cover" }}
                    onError={(e) => {
                      const el = e.currentTarget as HTMLImageElement;
                      el.src = "../assets/profilePic.png";
                    }}
                  />
                </IonAvatar>

                <div style={{ flex: 1, minWidth: 0, paddingRight: 84 }}>
                  <IonText>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "0.98rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {previewAuthorName}
                    </div>
                  </IonText>

                  {!!profile?.Company && (
                    <IonText
                      color="medium"
                    >
                      <div
                        style={{
                          fontSize: "0.88rem",
                          marginTop: 2,
                        }}
                      >
                        {profile.Company}
                      </div>
                    </IonText>
                  )}
                </div>

                <IonText
                  color="medium"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 2,
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {previewDate}
                </IonText>
              </div>

              <IonText>
                <h2
                  style={{
                    margin: "0 0 10px 0",
                    fontSize: "1.08rem",
                    lineHeight: 1.3,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {title.trim() || ""}
                </h2>
              </IonText>

              <IonText>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.6,
                    fontSize: "0.98rem",
                    marginBottom: imageUrl ? 14 : 0,
                  }}
                >
                  {body.trim() || "Your announcement preview will show here."}
                </div>
              </IonText>

              {!!imageUrl && (
                <div style={{ marginTop: 6 }}>
                  <img
                    src={imageUrl}
                    alt="Announcement preview"
                    style={{
                      width: "100%",
                      maxHeight: "360px",
                      objectFit: "cover",
                      borderRadius: "14px",
                      display: "block",
                    }}
                    onError={(e) => {
                      const el = e.currentTarget as HTMLImageElement;
                      el.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </IonContent>

      <IonFooter>
        <IonToolbar
          style={{
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 8,
            paddingBottom: 12,
            borderTop: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <IonButton
            expand="block"
            color="secondwarning"
            onClick={handleSend}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? isEditMode
                ? "Saving..."
                : "Sending..."
              : isEditMode
              ? "Save Changes"
              : "Send Announcement"}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default Announcement;
